import React, { useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

export default function IsoDateInput({
  value,
  onChange,
  placeholder = 'yyyy-mm-dd',
  className = '',
  onEnter,
  disabled = false,
  min,
  max,
}) {
  const nativeRef = useRef(null);
  const [text, setText] = useState(value || '');

  useEffect(() => {
    setText(value || '');
  }, [value]);

  const commit = (next) => {
    if (disabled) return;
    const trimmed = (next || '').trim();
    if (trimmed === '') {
      onChange('');            // allow clearing the filter
    } else if (ISO_RE.test(trimmed) && withinRange(trimmed)) {
      onChange(trimmed);
    } else {
      setText(value || '');    // invalid or out of range → snap back
    }
  };

  /* A typed date is held to the same bounds the picker enforces, so the
     keyboard can't get past a min/max the calendar would refuse. */
  function withinRange(iso) {
    if (min && iso < min) return false;
    if (max && iso > max) return false;
    return true;
  }

  const openPicker = () => {
    if (disabled) return;
    const el = nativeRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        /* fall through to focus */
      }
    }
    el.focus();
  };

  return (
    <div className="relative">
      <input
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={text}
        onChange={(e) => !disabled && setText(e.target.value)}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commit(e.target.value);
            if (onEnter) onEnter();
          }
        }}
        className={`${className} pr-8 ${disabled ? 'cursor-not-allowed' : ''}`}
      />
      <button
        type="button"
        onClick={openPicker}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-primary-600 transition-colors"
        aria-label="Pick date"
      >
        <Calendar size={14} />
      </button>
      <input
        ref={nativeRef}
        type="date"
        value={value || ''}
        min={min}
        max={max}
        onChange={(e) => !disabled && onChange(e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
        className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
      />
    </div>
  );
}